'use client';

import { c as _c } from "react/compiler-runtime";
import { Popover } from '@base-ui/react/popover';
import type { VirtualElement } from '@floating-ui/react';
import { Show, useObserveEffect, useSelector } from '@legendapp/state/react';
import { type ReactNode, useState } from 'react';
import { useIOsKeyboardHeight } from 'react-ios-keyboard-viewport';
import { batch } from '@legendapp/state';
import { type HeroBlockSearchFormTriggerId, useHeroBlockSearchFormState } from '@/components/forms/HeroBlockSearchForm/HeroBlockSearchFormProvider';
import { useSearchFromContext } from '@/components/forms/SearchFromProvider';
import { cn } from '@/lib/cn';
import './HeroBlockSearchFormPopoverViewport.css';
const MOBILE_TOP_OFFSET = 16;
export function HeroBlockSearchFormPopoverViewport() {
  const $ = _c(26);
  const {
    popover,
    triggerId$,
    open$,
    values,
    variant$,
    mode$,
    warnings$,
    isSm$
  } = useHeroBlockSearchFormState();
  const formState$ = useSearchFromContext();
  const triggerId = useSelector(triggerId$);
  const isVertical = useSelector(variant$) === "vertical";
  const isModal = useSelector(mode$) === "modal";
  const open = useSelector(open$);
  const [anchorEl, setAnchorEl] = useState();
  const iosKeyboardHeight = useIOsKeyboardHeight();
  let t0;
  if ($[0] !== isSm$ || $[1] !== mode$ || $[2] !== open$ || $[3] !== triggerId$) {
    t0 = () => {
      const anchorEl_0 = mode$.get() === "modal" || isSm$.get() ? viewPortCenterVirtualElement() : undefined;
      setAnchorEl(anchorEl_0 || undefined);
      triggerId$.set(null);
      open$.set(false);
    };
    $[0] = isSm$;
    $[1] = mode$;
    $[2] = open$;
    $[3] = triggerId$;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  useObserveEffect(t0);
  let t1;
  if ($[5] !== formState$ || $[6] !== mode$ || $[7] !== open$ || $[8] !== triggerId$ || $[9] !== values || $[10] !== warnings$) {
    t1 = (isOpen, eventDetails) => {
      if (mode$.get() === "modal") {
        setAnchorEl(viewPortCenterVirtualElement());
        triggerId$.set(null);
      }
      let triggerId_0 = eventDetails.trigger?.id as HeroBlockSearchFormTriggerId;
      const lastTrigger = triggerId$.get();
      if (isOpen && !formState$.fleetTypeValid()) {
        if (triggerId_0 === "locations") {
          warnings$.fleetType.set("Vehicle selection is required to see available locations.");
        }
        if (triggerId_0 === "dates") {
          warnings$.fleetType.set("Vehicle selection is required before selecting dates.");
        }
        triggerId_0 = "fleet-type";
        eventDetails.cancel();
      } else {
        if (isOpen && triggerId_0 !== "fleet-type" && !formState$.locationsValid()) {
          warnings$.pickUpBranch.set("Location is required before selecting dates.");
          triggerId_0 = "locations";
          eventDetails.cancel();
        }
      }
      batch(() => {
        triggerId$.set(triggerId_0 ?? null);
        open$.set(isOpen);
      });
      batch(() => {
        if ((!isOpen || triggerId_0 !== "fleet-type") && lastTrigger === "fleet-type") {
          const valid = Boolean(values.fleetType);
          if (!valid || eventDetails.reason === "close-press") {
            values.fleetType.set(formState$.fleetType.get());
            values.dropOffBranch.set(formState$.dropOffBranch.get());
          } else {
            if (valid) {
              formState$.fleetType.set(values.fleetType.get());
            }
          }
        }
        if ((!isOpen || triggerId_0 !== "locations") && lastTrigger === "locations") {
          const pickUpBranch = formState$.getBranchByCode(values.pickUpBranch.get());
          const dropOffBranch = formState$.getBranchByCode(values.dropOffBranch.get());
          const valid_0 = pickUpBranch?.siteCode && dropOffBranch?.siteCode && pickUpBranch.countryCode === dropOffBranch.countryCode;
          if (!valid_0 || eventDetails.reason === "close-press") {
            values.pickUpBranch.set(formState$.pickUpBranch.get());
            values.dropOffBranch.set(formState$.dropOffBranch.get());
          } else {
            if (valid_0) {
              formState$.pickUpBranch.set(pickUpBranch.siteCode);
              formState$.dropOffBranch.set(dropOffBranch.siteCode);
            }
          }
        }
      });
    };
    $[5] = formState$;
    $[6] = mode$;
    $[7] = open$;
    $[8] = triggerId$;
    $[9] = values;
    $[10] = warnings$;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  const handleOpenChange = t1;
  let t2;
  if ($[12] !== anchorEl || $[13] !== iosKeyboardHeight || $[14] !== isModal || $[15] !== isVertical || $[16] !== mode$ || $[17] !== triggerId) {
    t2 = t3 => {
      const {
        payload
      } = t3;
      return <Popover.Portal><Show if={() => mode$.get() === "modal"}>{_temp}</Show><Popover.Positioner side="top" align={isVertical ? "center" : getLandscapeAlignment(triggerId)} sideOffset={t4 => {
          const {
            positioner
          } = t4;
          if (isVertical) {
            return -(positioner.height / 2) - MOBILE_TOP_OFFSET;
          }
          return 0;
        }} anchor={anchorEl} collisionAvoidance={{
          side: isVertical ? "none" : "flip",
          align: "none",
          fallbackAxisSide: "none"
        }} className={cn("\n                            w-(--positioner-width)\n                            h-(--height-override)\n                            lg:h-(--positioner-height)\n                            transition-[top,left,right,bottom,transform]\n                            duration-(--animation-duration)\n                            ease-(--easing)\n                            data-instant:transition-none\n                            max-w-dvw\n                        ")} style={{
          "--easing": "cubic-bezier(0.22, 1, 0.36, 1)",
          "--animation-duration": "0.35s",
          "--mobile-offset": `${MOBILE_TOP_OFFSET}px`,
          "--height-override": "calc(100dvh - 2rem)"
        }}><Popover.Popup className={cn(isModal ? "relative" : "absolute", "origin-(--transform-origin)\n                                h-full\n                                lg:h-(--popup-height,auto)\n                                duration-(--animation-duration)\n                                ease-(--easing)\n                                data-starting-style:opacity-0\n                                data-starting-style:scale-[0.9]\n                                data-ending-style:opacity-0\n                                data-ending-style:scale-[0.9]\n                                data-instant:transition-none\n                            ")} style={iosKeyboardHeight ? {
            transform: `translateY(calc(-${iosKeyboardHeight}px))`
          } : undefined}><PopoverViewport>{payload}</PopoverViewport></Popover.Popup></Popover.Positioner></Popover.Portal>;
    };
    $[12] = anchorEl;
    $[13] = iosKeyboardHeight;
    $[14] = isModal;
    $[15] = isVertical;
    $[16] = mode$;
    $[17] = triggerId;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  let t3;
  if ($[19] !== handleOpenChange || $[20] !== isVertical || $[21] !== open || $[22] !== popover || $[23] !== t2 || $[24] !== triggerId) {
    t3 = <Popover.Root handle={popover} open={open} onOpenChange={handleOpenChange} triggerId={triggerId} modal={isVertical}>{t2}</Popover.Root>;
    $[19] = handleOpenChange;
    $[20] = isVertical;
    $[21] = open;
    $[22] = popover;
    $[23] = t2;
    $[24] = triggerId;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  return t3;
}
function _temp() {
  return <Popover.Backdrop className="fixed inset-0 bg-black/50 transition-opacity data-starting-style:opacity-0 data-ending-style:opacity-0" />;
}
interface PopoverViewportProps {
  children: ReactNode;
}
function PopoverViewport(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  const isModal = useSelector(useHeroBlockSearchFormState().mode$) === "modal";
  const Tag = isModal ? "div" : Popover.Viewport;
  let t1;
  if ($[0] !== Tag || $[1] !== children) {
    t1 = <Tag className="relative overflow-clip w-full h-full HeroBlockSearchFormPopoverViewport lg:pb-2 lg:pt-2">{children}</Tag>;
    $[0] = Tag;
    $[1] = children;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function viewPortCenterVirtualElement() {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return {
    getBoundingClientRect: () => ({
      width: 0,
      height: 0,
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
      top: window.innerHeight / 2,
      left: window.innerWidth / 2,
      right: window.innerWidth / 2,
      bottom: window.innerHeight / 2
    })
  } as VirtualElement;
}
function getLandscapeAlignment(triggerId: HeroBlockSearchFormTriggerId | null) {
  if (triggerId === 'dates') {
    return 'center';
  } else {
    return 'start';
  }
}